import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
} from "vue-router";
import LayoutViewVue from "../features/Shared/Views/LayoutView.vue";
import { useAuthStore } from "@/stores/auth";
import { useOperatorAdminStore } from "@/stores/operatorRole";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

const toast = useToast();

/**
 * Retry dynamic import with exponential backoff and cache busting
 * Fixes INTRANSVOICE-82 (158 events) - Failed to fetch dynamically imported module
 */
async function retryDynamicImport<T>(
  importFn: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await importFn();
    } catch (error: any) {
      const isLastAttempt = attempt === retries;
      const isChunkLoadError = 
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('text/html');

      if (!isChunkLoadError || isLastAttempt) {
        // Log to Sentry with context
        Sentry.captureException(error, {
          tags: {
            errorType: 'dynamic-import-failure',
            attempt: attempt.toString(),
            isChunkLoadError: isChunkLoadError.toString(),
          },
          extra: {
            errorMessage: error?.message,
            retries,
          },
        });

        if (isLastAttempt && isChunkLoadError) {
          // Show user-friendly message and suggest refresh
          toast.error(
            "Unable to load page. Please refresh your browser to get the latest version.",
            { timeout: 10000 }
          );
          
          // Optionally force reload after delay
          setTimeout(() => {
            if (confirm("A new version is available. Reload now?")) {
              window.location.reload();
            }
          }, 2000);
        }
        
        throw error;
      }

      // Wait with exponential backoff before retry
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
      
    }
  }
  
  throw new Error("Dynamic import failed after all retries");
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "app",
    component: LayoutViewVue,
    meta: {
      authGuard: true,
    },
    children: [
      {
        name: "Home",
        path: "",
        redirect: "dashboard",
      },
      {
        path: "dashboard/:jobsheet?",
        name: "Dashboard",
        component: () => retryDynamicImport(() => import("../features/Dashboard/DashboardView.vue")),
      },
      {
        path: "job-sheets/:jobsheet?",
        name: "Job Sheets",
        component: () => retryDynamicImport(() => import("../features/Jobs/JobSheetsView.vue")),
      },
      {
        path: "operators",
        name: "Operators",
        component: () => retryDynamicImport(() => import("../features/Operators/OperatorsView.vue")),
      },
      {
        path: "admin",
        name: "Admin",
        component: () => retryDynamicImport(() => import("../features/Admin/AdminView.vue")),
      },
      {
        path: "users",
        name: "Users",
        component: () => retryDynamicImport(() => import("../features/Users/UsersView.vue")),
      },
      {
        path: "products",
        name: "Products",
        component: () => retryDynamicImport(() => import("../features/Products/ProductsView.vue")),
      },
      {
        path: "reports",
        name: "Reports",
        component: () => retryDynamicImport(() => import("../features/Reports/ReportsView.vue")),
        meta: {
          requiresAuth: true,
          adminOnly: true,
          title: 'Reports'
        }
      },
      {
        path: "invoices",
        name: "Invoices",
        component: () => retryDynamicImport(() => import("../features/Invoices/InvoiceView.vue")),
        children: [
          {
            path: ":invoiceid",
            name: "Invoice",
            component: () => retryDynamicImport(() => import("../features/Invoices/InvoiceView.vue")),
          },
        ],
      },
      {
        path: "settings",
        name: "Settings",
        component: () => retryDynamicImport(() => import("../features/Settings/SettingsView.vue")),
      },
      {
        path: "company-settings",
        name: "CompanySettings",
        component: () => retryDynamicImport(() => import("../features/Settings/CompanySettingsView.vue")),
      },
      {
        path: "xero-callback",
        name: "XeroCallback",
        component: () => retryDynamicImport(() => import("../features/Settings/XeroCallbackView.vue")),
      },
    ],
  },
  {
    path: "/operator/:envid",
    name: "operator",
    component: () => retryDynamicImport(() => import("../features/OperatorRole/OperatorRoleView.vue")),
    meta: {
      authGuard: true,
    },
  },
  {
    path: "/operator/:envid/jobsheet/:jobsheet",
    name: "operator-jobsheet",
    component: () => retryDynamicImport(() => import("../features/OperatorRole/OperatorRoleJobView.vue")),
    meta: {
      authGuard: true,
    },
  },
  {
    path: "/signin/:companyname",
    name: "operator-signin",
    component: () => retryDynamicImport(() => import("../features/Signin/SigninOperatorView.vue")),
  },
  {
    path: "/signin",
    name: "signin",
    component: () => retryDynamicImport(() => import("../features/Signin/SigninView.vue")),
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => retryDynamicImport(() => import("../features/Signin/ResetPasswordView.vue")),
  },
  {
    path: "/signout",
    name: "Sign out",
    component: () => retryDynamicImport(() => import("../features/Signin/SignoutView.vue")),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { getCurrentUser, loadUserIntoState, authenticatedUser } =
    useAuthStore();

  const { isOperator } = useOperatorAdminStore();

  // Check admin-only routes
  if (to.meta.adminOnly && isOperator) {
    // Redirect operators away from admin-only pages
    next({ name: "operator", params: { envid: "default" } });
    return;
  }

  if (!to.meta.authGuard || authenticatedUser) {
    next();
  } else {
    const currentUser = await getCurrentUser();
    if (currentUser) {
      loadUserIntoState(currentUser);

      // Check admin-only routes after loading user
      if (to.meta.adminOnly && isOperator) {
        next({ name: "operator", params: { envid: "default" } });
        return;
      }

      next();
    } else {
      next({ name: "signin" });
    }
  }
});

export default router;
